import * as React from "react";
import {
  Box,
  Button,
  Collapse,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";

import discountIcon from "../../../assets/svg/discount.svg";
import { CaretDown, CaretUp } from "@phosphor-icons/react";
import { useCartContext } from "../../../hooks/CartContext";

export const CouponItem = ({
  item,
  index,
  couponDiscount,
  removeCouponHandler,
  applyCouponHandler,
  isDisabled = false,
  disabledReason = "",
}) => {
  const theme = useTheme();
  const [toggleDetails, setToggleDetails] = useState(false);
  const { applyDiscount } = useCartContext();

  const isApplied =
    couponDiscount?.amount > 0 && couponDiscount?.code === item?.promo_code;

  return (
    <CouponDashedCard
      key={index}
      style={{ alignItems: toggleDetails ? "flex-start" : "center" }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
        <Box
          component="img"
          src={discountIcon}
          alt="Coupon Icon"
          sx={{
            height: { xs: "30px", md: "40px" },
            mr: "10px",
            // ✅ Fade icon if disabled
            opacity: isDisabled ? 0.4 : 1,
          }}
        />

        <Box>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              fontSize: { xs: "14px", md: "16px" },
              display: "flex",
              alignItems: "center",
              // ✅ Fade text if disabled
              opacity: isDisabled ? 0.5 : 1,
            }}
          >
            <Box component="span">
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: "14px", md: "16px" },
                  pl: 1,
                }}
              >
                {parseTC(item, applyDiscount)}
              </Typography>
              {item?.end_date && (
                <Typography
                  sx={{
                    fontSize: "11px",
                    mt: 0.5,
                    ml: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  Valid till{" "}
                  {new Date(item.end_date).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })}
                </Typography>
              )}
            </Box>
          </Typography>

          <Typography
            onClick={() => setToggleDetails((prev) => !prev)}
            variant="body2"
            sx={{
              fontWeight: 500,
              fontSize: { xs: "11px", md: "12px" },
              mt: 0.5,
              alignItems: "center",
              display: "flex",
              gap: 1,
              cursor: "pointer",
              ml: 1,
              color: "blue",
            }}
          >
            {`Show ${!toggleDetails ? "more" : "less"}`}
            {!toggleDetails ? <CaretDown size={16} /> : <CaretUp size={16} />}
          </Typography>

          <Collapse in={toggleDetails}>
            <ul style={{ paddingLeft: "20px", margin: 0 }}>
              {[
                ...(item?.terms_conditions
                  ?.replace(/\\n/g, "\n")
                  ?.split("\n")
                  ?.filter((line) => line.trim() !== "") || []),
                ...(item?.promo_applicable_count
                  ? [
                      `You can use this promo ${item.promo_applicable_count} time(s).`,
                    ]
                  : []),
                "This coupon is not applicable on combo products.",
              ].map((line, index) => (
                <li key={index}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "12px",
                      fontWeight: 400,
                      color: "var(--neutral-800)",
                    }}
                  >
                    {line}
                  </Typography>
                </li>
              ))}
            </ul>

            {isDisabled && (
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#FF272F",
                  mt: 0.5,
                  ml: 1,
                  fontWeight: 500,
                }}
              >
                 Your product discounts are already better than this coupon
              </Typography>
            )}
          </Collapse>
        </Box>
      </Box>

      {/* ✅ Show Remove if already applied */}
      {isApplied ? (
        <Button
          onClick={removeCouponHandler}
          variant="outlined"
          sx={{
            color: "var(--primary-red-base)",
            borderColor: "var(--primary-red-base)",
            fontWeight: 500,
            textTransform: "none",
            fontSize: { xs: "13px", md: "14px" },
            padding: "16px 32px",
            borderRadius: "4px",
            height: { xs: "30px", md: "36px" },
          }}
        >
          Remove
        </Button>
      ) : (
        // ✅ Apply button — light red + no click if disabled
        <Button
          onClick={() => {
            if (!isDisabled) applyCouponHandler(item);
          }}
          variant="outlined"
          sx={{
            // ✅ Light red when disabled, normal red when enabled
            color: isDisabled
              ? "rgba(211, 47, 47, 0.35)"
              : "var(--primary-red-base)",
            borderColor: isDisabled
              ? "rgba(211, 47, 47, 0.25)"
              : "var(--primary-red-base)",
            fontWeight: 500,
            textTransform: "none",
            fontSize: { xs: "13px", md: "14px" },
            padding: "16px 32px",
            borderRadius: "4px",
            height: { xs: "30px", md: "36px" },
            // ✅ Block all pointer interaction when disabled
            pointerEvents: isDisabled ? "none" : "auto",
            cursor: isDisabled ? "default" : "pointer",
            "&:hover": {
              backgroundColor: isDisabled
                ? "transparent"
                : "rgba(211, 47, 47, 0.04)",
              borderColor: isDisabled
                ? "rgba(211, 47, 47, 0.25)"
                : "var(--primary-red-base)",
            },
          }}
        >
          Apply
        </Button>
      )}
    </CouponDashedCard>
  );
};

const parseTC = (item, applyDiscount) => {
  return item?.type === "PERCENTAGE" ? (
    <>
      Save <strong>{item?.discount}%</strong> with "
      <strong>{item?.promo_code}</strong>"
    </>
  ) : (
    <>
      Save <strong>₹{item?.discount}</strong> with "
      <strong>{item?.promo_code}</strong>"
    </>
  );
};

const CouponDashedCard = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  border: "2px dashed var(--neutral-400)",
  borderRadius: "8px",
  marginBottom: "12px",
  padding: "20px",
  "&:hover": {
    border: "2px dashed var(--neutral-700)",
    boxShadow: "0px 4px 12px rgba(12, 12, 13, 0.05)",
    transform: "scale(0.99, 0.99)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "12px 16px",
  },
}));
